import { DashboardCoordinatorMiddleComponent } from './../dashboard-coordinator-middle/dashboard-coordinator-middle.component';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CaseService } from 'src/app/core/service/case.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { TaskService } from 'src/app/core/service/task.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Subscription } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';

interface Totals
{
  patients: number;
  cases: number;
}

@Component({
  standalone: false,
  selector: 'app-dashboard-coordinator-top-left',
  templateUrl: './dashboard-coordinator-top-left.component.html',
  styleUrls: ['./dashboard-coordinator-top-left.component.scss'],
  providers: [DatePipe]
})
export class DashboardCoordinatorTopLeftComponent implements OnInit, OnDestroy
{
  @Input() public filter: any;
  @Input() public performanceComponent: DashboardCoordinatorMiddleComponent;

  /**
   * Current logged in userId
   */
  public currentUserId: number;
  public topLeftLoading = true;

  public caseData = [];
  public patientData = [];
  public taskData = [];

  public totalCases = 0;
  public totalPatients = 0;
  public totalTasks = 0;
  public totalTasksLoaded = 0;

  public patientLabel = {};

  public caseList: any[];

  private messagingSubscription: Subscription;

  constructor(
    private caseService: CaseService,
    private taskService: TaskService,
    private settingService: SettingService,
    private messengerService: MessengerService,
    private authenticationService: AuthenticationService
  ) { }

  public ngOnInit(): void
  {
    this.setTotals(0, 0, 0);

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.caseService.getCasesByManager().subscribe({
      next: (groupedCases) =>
      {
        this.caseList = groupedCases;
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const totals = this.countPatientsAndCases(this.caseList['data']);
        this.setCaseData(totals.cases);
        this.setPatientData(totals.patients);
        this.topLeftLoading = false;
      }
    });

    const currentUser = this.authenticationService.currentUser;
    this.taskService.getTasksSummaryForAssignee(currentUser.userName).subscribe({
      next: (data: any) =>
      {
        this.totalTasksLoaded = data.totalTasks;
        this.setTaskData(data.totalTasks);
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'refreshDashboardCounts')
        {
          this.refreshCounts();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  private refreshCounts(): void
  {
    if (this.caseList !== undefined)
    {
      this.setTotals(0, 0, 0);

      if (this.filter !== undefined &&
        ((this.filter.filterCategory !== null && this.filter.filterCategory.length > 0) ||
          (this.filter.filterByStatus !== null && this.filter.filterByStatus.length > 0) ||
          (this.filter.filterByProgramName !== null && this.filter.filterByProgramName.length > 0) ||
          (this.filter.filterByChannelName !== null && this.filter.filterByChannelName.length > 0)))
      {
        let totalPatients = 0;
        let totalTasks = 0;
        let totalCases = 0;

        // eslint-disable-next-line @typescript-eslint/dot-notation
        this.caseList['data'].forEach(group =>
        {
          let filterCases = group.cases;

          if (this.filter.filterByStatus !== null && this.filter.filterByStatus.length > 0)
          {
            filterCases = filterCases.filter(() => this.filter.filterByStatus.includes(group.status));
          }

          if (this.filter.filterCategory !== null && this.filter.filterCategory.length > 0)
          {
            filterCases = filterCases.filter(x => this.filter.filterCategory.includes(x.c));
          }

          if (this.filter.filterByProgramName !== null && this.filter.filterByProgramName.length > 0)
          {
            filterCases = filterCases.filter(x => x.pname.toLowerCase().includes(this.filter.filterByProgramName.toLowerCase()));
          }

          if (this.filter.filterByChannelName !== null && this.filter.filterByChannelName.length > 0)
          {
            filterCases = filterCases.filter(x => x.cname.toLowerCase().includes(this.filter.filterByChannelName.toLowerCase()));
          }

          const totals = this.countPatientsAndCases(filterCases);
          totalPatients += totals.patients;
          totalCases += totals.cases;
          totalTasks = this.countTotalTasksFromPerformance();
        });
        this.setTotals(totalTasks, totalCases, totalPatients);
      }
      else
      {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const totals = this.countPatientsAndCases(this.caseList['data']);
        this.setTotals(this.totalTasksLoaded, totals.cases, totals.patients);
      }
      this.topLeftLoading = false;
    }
  }

  private setTaskData(totalTasks: number): void
  {
    this.totalTasks = totalTasks;
    this.taskData = [
      {
        tasks: `${totalTasks}`,
        value: totalTasks === 0 ? 1 : totalTasks,
        category: 'Tasks',
        color: '#ffffff'
      }
    ];
  }

  private setCaseData(totalCases: number): void
  {
    this.totalCases = totalCases;
    this.caseData = [
      {
        cases: `${totalCases}`,
        value: totalCases === 0 ? 1 : totalCases,
        category: 'Cases',
        color: '#ffffff'
      }
    ];
  }

  private setPatientData(totalPatients: number): void
  {
    this.totalPatients = totalPatients;
    this.patientData = [
      {
        patients: `${totalPatients}`,
        value: totalPatients === 0 ? 1 : totalPatients,
        category: 'Patients',
        color: '#ffffff'
      }
    ];
  }

  private setTotals(tasks: number, cases: number, patients: number): void
  {
    this.setTaskData(tasks);
    this.setCaseData(cases);
    this.setPatientData(patients);
  }

  private countPatientsAndCases(caseList: any): Totals
  {
    const patients: Set<string> = new Set<string>();

    let totalPatients = 0;
    let totalCases = 0;

    caseList.forEach((d: any) =>
    {
      if (d.cases !== undefined)
      {
        d.cases.forEach((c: any) =>
        {
          if (!patients.has(c.m))
          {
            patients.add(c.m);
            totalPatients++;
          }
        });
        totalCases += d.cases.length;
      }
      else
      {
        if (!patients.has(d.m))
        {
          patients.add(d.m);
          totalPatients++;
        }
        totalCases = caseList.length;
      }
    });

    return {
      patients: totalPatients,
      cases: totalCases
    };
  }

  private countTotalTasksFromPerformance(): number
  {
    // Tasks count is the accumulated totalTasks from loaded performance
    let processList: any[] = [];
    let totalTasks = 0;

    // Use the proper list if grouped
    if (this.performanceComponent.groupedPerformanceList !== undefined &&
      this.performanceComponent.groupedPerformanceList.length > 0)
    {
      processList = this.performanceComponent.groupedPerformanceList;
    }
    else if (this.performanceComponent.performanceList !== undefined &&
      this.performanceComponent.performanceList.length > 0)
    {
      // Insert as an object to use generic count method below
      processList.push({ performanceList: this.performanceComponent.performanceList });
    }

    processList.forEach(d =>
    {
      let filteredPerformanceList = d.performanceList;

      // Filter channel by status
      if (this.filter.filterByStatus !== null && this.filter.filterByStatus.length > 0)
      {
        filteredPerformanceList = filteredPerformanceList.filter(x => this.filter.filterByStatus.includes(x.channelStatus));
      }

      // Filter program by category
      if (this.filter.filterCategory !== null && this.filter.filterCategory.length > 0)
      {
        filteredPerformanceList = filteredPerformanceList.filter(x => this.filter.filterCategory.includes(x.programCategory));
      }

      // Filter by program name
      if (this.filter.filterByProgramName !== null && this.filter.filterByProgramName.length > 0)
      {
        filteredPerformanceList = filteredPerformanceList.filter(x => this.filter.filterByProgramName.toLowerCase()
          .includes(x.programName.toLowerCase()));
      }

      // Filter by channel name
      if (this.filter.filterByChannelName !== null && this.filter.filterByChannelName.length > 0)
      {
        filteredPerformanceList = filteredPerformanceList.filter(x => this.filter.filterByChannelName.toLowerCase()
          .includes(x.channelName.toLowerCase()));
      }

      // Calculate total tasks
      filteredPerformanceList.forEach(f =>
      {
        totalTasks += f.totalTasks;
      });
    });

    return totalTasks;
  }
}
